// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { CaseTemplateOutput } from 'thehive-sdk';
import { trimTags, useLocalMessage } from '@Helpers/';
import CaseTemplateForm, { CaseTemplateValuesForm } from '@Organisms/CaseTemplate/CaseTemplateManage/CaseTemplateForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';
import { CaseTemplateEventList } from '../eventList';

interface CaseTemplateManageProps {
    onCreate?: CaseTemplateEventList['onCreate'];
    onEdit?: CaseTemplateEventList['onEdit'];
    triggerElem?: ReactElement;
    edit?: boolean;
    defaultValues?: CaseTemplateOutput;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

const CaseTemplateManage = ({
    onCreate,
    onEdit,
    triggerElem,
    edit = false,
    defaultValues,
    visible,
    onVisibleChange,
}: CaseTemplateManageProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinish = (caseTemplate: CaseTemplateValuesForm): Promise<any> => {
        if (onCreate) {
            return onCreate({
                ...caseTemplate,
                name: caseTemplate.name?.trim(),
                displayName: caseTemplate.displayName?.trim(),
                titlePrefix: caseTemplate.titlePrefix?.trim(),
                description: caseTemplate.description?.trim(),
                tags: trimTags(caseTemplate.tags || []),
                pageTemplateIds: caseTemplate.pageTemplateIds,
            });
        }

        if (onEdit) {
            return onEdit(
                {
                    ...caseTemplate,
                    name: caseTemplate.name?.trim(),
                    displayName: caseTemplate.displayName?.trim() || undefined,
                    titlePrefix: caseTemplate.titlePrefix?.trim() || '',
                    description: caseTemplate.description?.trim() || undefined,
                    pageTemplateIds: caseTemplate.pageTemplateIds,
                    tags: caseTemplate?.tags && trimTags(caseTemplate.tags || defaultValues?.tags),
                },
                defaultValues?._id || '',
            );
        }

        return Promise.resolve();
    };

    const tradLabelKey = edit ? 'edit' : 'create';

    return (
        <THDrawer
            title={i(`case-template.${tradLabelKey}.drawer.title`)}
            triggerElem={triggerElem}
            afterVisibleChange={(visible) => {
                form.resetFields();
                onVisibleChange?.(visible);
            }}
            className="drawer-form"
            width="50vw"
            disableClose={fieldsTouched}
            visible={visible}
        >
            {(toggleDrawer) => (
                <CaseTemplateForm
                    onCancel={() => {
                        form.resetFields();
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    isValid={isValid && fieldsTouched}
                    onFinish={(values) =>
                        onFinish(values)?.then((data = {}) => {
                            if (data && 'errorCode' in data) {
                                return;
                            }

                            toggleDrawer();
                            setFieldsTouched(false);
                        })
                    }
                    form={form}
                    defaultValues={defaultValues}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                />
            )}
        </THDrawer>
    );
};

export default CaseTemplateManage;
